import React, { useState, useEffect, useRef } from 'react';
import { FiSend, FiMic, FiMicOff, FiVolume2, FiVolumeX } from 'react-icons/fi';
import Api from '../../Api';
import { PageComponent } from '../../components';
import './style.css';

export default function BusinessAgent() {
    const [messages, setMessages] = useState([]);
    const [inputText, setInputText] = useState('');
    const [loading, setLoading] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [voiceEnabled, setVoiceEnabled] = useState(false); // To toggle auto-speak globally or per session
    const [isSpeaking, setIsSpeaking] = useState(false);
    
    const messagesEndRef = useRef(null);
    const recognitionRef = useRef(null);
    const currentAudioRef = useRef(null);

    // Auto-scroll to bottom
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, loading]);

    // Initial greeting
    useEffect(() => {
        const initialMsg = 'Olá! Sou seu BusinessAgent. Como posso ajudar na gestão da sua empresa hoje?';
        setMessages([
            { role: 'assistant', content: initialMsg, timestamp: new Date() }
        ]);
    }, []);

    // Initialize Speech Recognition
    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (SpeechRecognition) {
            const recognition = new SpeechRecognition();
            recognition.continuous = false; // Keep false to avoid hanging
            recognition.interimResults = true; // CHANGED: Enable interim results to see live transcription
            recognition.lang = 'pt-BR';

            // Timer to detect silence
            let silenceTimer = null;

            recognition.onstart = () => {
                setIsListening(true);
            };

            recognition.onend = () => {
                // Only stop if we manually stopped or silence timer triggered
                // If it stopped but we didn't send yet, it might be just a pause.
                // However, continuous=false usually stops after a sentence.
                // We'll rely on the silence timer in onresult to determine "done".
                setIsListening(false);
            };

            recognition.onresult = (event) => {
                let finalTranscript = '';
                let interimTranscript = '';

                for (let i = event.resultIndex; i < event.results.length; ++i) {
                    if (event.results[i].isFinal) {
                        finalTranscript += event.results[i][0].transcript;
                    } else {
                        interimTranscript += event.results[i][0].transcript;
                    }
                }

                if (interimTranscript) {
                    setInputText(interimTranscript);
                    
                    // Reset silence timer on any input
                    if (silenceTimer) clearTimeout(silenceTimer);
                }

                if (finalTranscript) {
                    setInputText(finalTranscript);
                    
                    // Wait 1.5 seconds of silence/finality before sending
                    if (silenceTimer) clearTimeout(silenceTimer);
                    silenceTimer = setTimeout(() => {
                        handleSend(finalTranscript, true);
                    }, 1500); 
                }
            };

            recognition.onerror = (event) => {
                console.error("Speech recognition error", event.error);
                setIsListening(false);
            };

            recognitionRef.current = recognition;
        } else {
            console.warn("Speech Recognition not supported in this browser.");
        }

        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.stop();
            }
            if (currentAudioRef.current) {
                currentAudioRef.current.pause();
                currentAudioRef.current = null;
            }
        };
    }, []);

    const toggleListening = () => {
        if (!recognitionRef.current) {
            alert("Seu navegador não suporta reconhecimento de voz.");
            return;
        }

        if (isListening) {
            recognitionRef.current.stop();
        } else {
            // Stop speaking if currently speaking
            if (isSpeaking) {
                stopSpeaking();
            }
            setVoiceEnabled(true); // Enable voice response if user uses mic
            recognitionRef.current.start();
        }
    };

    const playAudio = (base64Audio) => {
        if (!base64Audio) return;
        
        stopSpeaking(); // Stop any current audio

        try {
            const audio = new Audio(`data:audio/mp3;base64,${base64Audio}`);
            currentAudioRef.current = audio;
            
            audio.onplay = () => setIsSpeaking(true);
            audio.onended = () => {
                setIsSpeaking(false);
                currentAudioRef.current = null;
            };
            audio.onerror = (e) => {
                console.error("Audio playback error:", e);
                setIsSpeaking(false);
                currentAudioRef.current = null;
            };
            
            audio.play().catch(e => console.error("Error playing audio:", e));
        } catch (e) {
            console.error("Failed to create audio object:", e);
        }
    };

    const stopSpeaking = () => {
        if (currentAudioRef.current) {
            currentAudioRef.current.pause();
            currentAudioRef.current = null;
            setIsSpeaking(false);
        }
    };

    const handleSend = async (textToSend = inputText, fromVoice = false) => {
        if (!textToSend.trim() || loading) return;

        // If manual send, clear input. If voice, input is already textToSend
        if (!fromVoice) {
            setInputText('');
        } else {
            setInputText(''); // Clear after sending
        }
        
        const userMessage = { role: 'user', content: textToSend, timestamp: new Date() };
        setMessages(prev => [...prev, userMessage]);
        setLoading(true);

        try {
            // Send conversation history to backend
            const history = messages.map(m => ({ role: m.role, content: m.content }));
            
            // Determine if we should request audio
            const shouldRequestAudio = voiceEnabled || fromVoice;

            const result = await Api.chatWithAgent(textToSend, history, shouldRequestAudio);
            
            let responseText = "Recebi uma resposta vazia.";
            if (result && result.response) {
                responseText = result.response;
            }

            setMessages(prev => [...prev, { role: 'assistant', content: responseText, timestamp: new Date() }]);

            // Play audio if returned
            if (result.audio) {
                playAudio(result.audio);
            }

        } catch (error) {
            console.error(error);
            const errorMsg = 'Desculpe, ocorreu um erro ao processar sua solicitação.';
            setMessages(prev => [...prev, { role: 'assistant', content: errorMsg, timestamp: new Date() }]);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const formatTime = (date) => {
        if (!date) return '';
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <PageComponent title="Business Agent - IA Gerencial" fullHeight authenticatedPage={true}>
            <div className="business-agent-page">
                <div className="chat-header">
                    <div className="agent-avatar">
                        IA
                    </div>
                    <div className="agent-info">
                        <h3>ClockFy Agent</h3>
                        <div className="agent-status">
                            <div className="status-dot"></div>
                            Online
                        </div>
                    </div>
                    <div style={{ marginLeft: 'auto' }}>
                         {/* Toggle Voice Output Globally */}
                         <button 
                            className="action-button" 
                            onClick={() => {
                                if (isSpeaking) stopSpeaking();
                                setVoiceEnabled(!voiceEnabled);
                            }}
                            title={voiceEnabled ? "Voz ativada" : "Voz desativada"}
                        >
                            {voiceEnabled ? <FiVolume2 size={20} color="var(--primary-color)" /> : <FiVolumeX size={20} />}
                        </button>
                    </div>
                </div>

                <div className="chat-history">
                    {messages.map((msg, index) => (
                        <div key={index} className={`message-wrapper ${msg.role}`}>
                            <div className={`message-bubble ${msg.role}`}>
                                {msg.content && msg.content.split('\n').map((line, i) => (
                                    <p key={i} style={{ margin: '4px 0' }}>{line}</p>
                                ))}
                            </div>
                            <span className="message-time">
                                {formatTime(msg.timestamp || new Date())}
                            </span>
                        </div>
                    ))}
                    {loading && (
                        <div className="loading-indicator">
                            <div className="typing-dot"></div>
                            <div className="typing-dot"></div>
                            <div className="typing-dot"></div>
                            <span style={{ marginLeft: '8px' }}>Processando...</span>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>
                
                <div className={`voice-feedback ${isListening ? 'visible' : ''}`}>
                    {isListening ? "Ouvindo... Fale agora" : ""}
                </div>

                <div className="input-area">
                    <button 
                        className={`action-button mic-button ${isListening ? 'listening' : ''}`}
                        onClick={toggleListening}
                        title="Falar com a IA"
                    >
                        {isListening ? <FiMicOff size={20} /> : <FiMic size={20} />}
                    </button>
                    
                    <div className="input-container">
                        <input 
                            type="text" 
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={isListening ? "Ouvindo..." : "Digite sua mensagem..."}
                            disabled={loading || isListening}
                        />
                    </div>

                    <button 
                        className="action-button send-button" 
                        onClick={() => handleSend()} 
                        disabled={loading || (!inputText.trim() && !isListening)}
                    >
                        <FiSend size={20} />
                    </button>
                </div>
            </div>
        </PageComponent>
    );
}
